/**
 * Same contract as `src/utils/allowedEmails.js` — GET/PATCH `/api/allowed-emails`.
 */

import { DEFAULT_ROLE, VALID_ROLES } from '../config/appRoles'

const API_URL = '/api/allowed-emails'

export interface AllowedUserRow {
  email: string
  role: string
}

export async function fetchAllowedUsers(): Promise<AllowedUserRow[]> {
  try {
    const res = await fetch(API_URL)
    const data = (await res.json().catch(() => ({}))) as {
      users?: unknown[]
    }
    const list = Array.isArray(data?.users) ? data.users : []
    return list
      .map((u: unknown) => {
        const row = u as { email?: string; role?: string }
        const email =
          typeof row?.email === 'string' ? row.email.trim().toLowerCase() : ''
        const role =
          typeof row?.role === 'string' ? row.role.trim().toLowerCase() : ''
        return {
          email,
          role: role && VALID_ROLES.includes(role) ? role : DEFAULT_ROLE,
        }
      })
      .filter((u: AllowedUserRow) => u.email)
  } catch {
    return []
  }
}

export async function saveAllowedUsers(
  users: AllowedUserRow[],
  userEmail?: string
): Promise<{ ok?: boolean; users?: AllowedUserRow[] }> {
  const list = (Array.isArray(users) ? users : [])
    .map(u => {
      const email =
        typeof u?.email === 'string' ? u.email.trim().toLowerCase() : ''
      const role =
        typeof u?.role === 'string' ? u.role.trim().toLowerCase() : ''
      return {
        email,
        role: role && VALID_ROLES.includes(role) ? role : DEFAULT_ROLE,
      }
    })
    .filter(u => u.email)
  const seen = new Set<string>()
  const unique = list.filter(u => {
    if (seen.has(u.email)) return false
    seen.add(u.email)
    return true
  })
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (userEmail && typeof userEmail === 'string') {
    headers['X-User-Email'] = userEmail.trim()
  }
  const res = await fetch(API_URL, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ users: unique }),
  })
  const data = (await res.json().catch(() => ({}))) as {
    error?: string
    ok?: boolean
    users?: AllowedUserRow[]
  }
  if (!res.ok) throw new Error(data.error || `Save failed (${res.status})`)
  return data
}
